<template>
  <div>
    <div
      class="d-flex flex-column flex-md-row justify-content-around align-items-center bg-white"
    >
      <img
        :src="character.race.img"
        :alt="character.race.name"
        style="max-height: 200px;"
      />
      <img
        :src="`/assets/img/game_resources/classes/${character.class.slug}.jpg`"
        :alt="character.class.name"
        style="max-height: 200px;"
      />
      <h4 class="text-uppercase mb-3" style="color: #444;">
        {{ character.name }}
      </h4>
    </div>

    <table id="char_table" class="table table-dark table-striped">
      <tbody>
        <tr>
          <td colspan="3" class="text-center">
            <span class="text-uppercase mr-2">Raça:</span>
            {{ character.race.name }}
          </td>
          <td colspan="3" class="text-center">
            <span class="text-uppercase mr-2">Classe:</span>
            {{ character.class.name }}
          </td>
        </tr>

        <tr>
          <td colspan="3" class="text-center">
            <span class="text-uppercase mr-2">Nível de Raça:</span>
            {{ character.raceLevel.name }}
          </td>
          <td colspan="3" class="text-center">
            <span class="text-uppercase mr-2">Nível de Classe:</span>
            {{ character.classLevel.name }}
          </td>
        </tr>

        <tr>
          <td colspan="2" class="text-center">
            <span class="text-uppercase mr-2">PV:</span>
            {{ getAttribute('pv') }}
          </td>
          <td colspan="2" class="text-center">
            <span class="text-uppercase mr-2">PH:</span>
            {{ getAttribute('ph') }}
          </td>
          <td colspan="2" class="text-center">
            <span class="text-uppercase mr-2">Mov:</span>
            {{ getBonusAttribute('mov') + character.race.mov }}
          </td>
        </tr>

        <tr>
          <td class="text-center text-uppercase font-weight-bold" colspan="6">
            Atributos
          </td>
        </tr>

        <tr>
          <td colspan="3" class="text-center">
            <span class="text-uppercase mr-2">
              {{ getAttributeLabel('str') }}:
            </span>
            {{ getAttribute('str') }}
          </td>
          <td colspan="3" class="text-center">
            <span class="text-uppercase mr-2">
              {{ getAttributeLabel('res') }}:
            </span>
            {{ getAttribute('res') }}
          </td>
        </tr>

        <tr>
          <td colspan="3" class="text-center">
            <span class="text-uppercase mr-2">
              {{ getAttributeLabel('dex') }}:
            </span>
            {{ getAttribute('dex') }}
          </td>
          <td colspan="3" class="text-center">
            <span class="text-uppercase mr-2">
              {{ getAttributeLabel('agi') }}:
            </span>
            {{ getAttribute('agi') }}
          </td>
        </tr>

        <tr>
          <td colspan="3" class="text-center">
            <span class="text-uppercase mr-2">
              {{ getAttributeLabel('wis') }}:
            </span>
            {{ getAttribute('wis') }}
          </td>
          <td colspan="3" class="text-center">
            <span class="text-uppercase mr-2">
              {{ getAttributeLabel('int') }}:
            </span>
            {{ getAttribute('int') }}
          </td>
        </tr>

        <tr>
          <td colspan="3" class="text-center">
            <span class="text-uppercase mr-2">Bônus de Dano:</span>
            {{ getBonusAttribute('dmg') }}
          </td>
          <td colspan="3" class="text-center">
            <span class="text-uppercase mr-2">Bônus de Defesa:</span>
            {{ getBonusAttribute('def') }}
          </td>
        </tr>

        <tr>
          <td class="text-center text-uppercase font-weight-bold" colspan="6">
            Equipamentos
          </td>
        </tr>

        <tr>
          <td colspan="6">
            <span class="text-uppercase mr-2">Arma Primária:</span>
            {{ character.primaryWeapon.name }}
            ({{ getAttributeLabel(character.class.primary) }}) (Ataque
            {{ character.primaryWeapon.desc }}) (Alcance
            {{ character.primaryWeapon.range }})
            <template v-if="character.primaryWeapon.effect">
              <br />
              <small>({{ character.primaryWeapon.effect }})</small>
            </template>
          </td>
        </tr>

        <tr>
          <td colspan="6">
            <span class="text-uppercase mr-2">Arma Secundária:</span>
            {{ character.secondaryWeapon.name }}
            ({{ getAttributeLabel(character.class.secondary) }}) (Ataque
            {{ character.secondaryWeapon.desc }}) (Alcance
            {{ character.secondaryWeapon.range }})
          </td>
        </tr>

        <tr>
          <td colspan="6">
            <span class="text-uppercase mr-2">Armadura:</span>
            {{ character.armor.name }}
            (Defesa {{ character.armor.desc }})
          </td>
        </tr>

        <tr>
          <td colspan="6">
            <span class="text-uppercase mr-2">Acessório:</span>
            {{ character.accessory.name }}
            <template v-if="character.accessory.desc">
              <br />
              <small>({{ character.accessory.desc }})</small>
            </template>
          </td>
        </tr>

        <tr>
          <td class="text-center text-uppercase font-weight-bold" colspan="6">
            Habilidades de Raça
          </td>
        </tr>

        <tr v-for="skill in raceSkills" :key="skill.name">
          <td colspan="6">
            {{ skill.name }} ({{ getSkillTypeLabel(skill.type) }})
            <br />
            <small>{{ skill.desc }}</small>
          </td>
        </tr>

        <tr>
          <td class="text-center text-uppercase font-weight-bold" colspan="6">
            Habilidades de Classe
          </td>
        </tr>

        <tr v-for="skill in classSkills" :key="skill.name">
          <td colspan="6">
            {{ skill.name }} - {{ getSkillCostDescription(skill.cost) }} ({{
              getSkillTypeLabel(skill.type)
            }})
            <br />
            <small>{{ skill.desc }}</small>
          </td>
        </tr>

        <template v-if="showSpecialSkill">
          <tr>
            <td class="text-center text-uppercase font-weight-bold" colspan="6">
              Habilidade Especial
            </td>
          </tr>

          <tr>
            <td colspan="6">
              {{ specialSkill.name }} ({{
                getSkillTypeLabel(specialSkill.type)
              }})
              <br />
              <small>{{ specialSkill.desc }}</small>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
import { getRandomWithRange } from '../utils/randomPicker'
import {
  getAttributeLabel,
  getSkillTypeLabel,
  getSkillCostDescription
} from '../utils/texts'

export default {
  name: 'character-table',

  props: {
    character: { type: Object, required: true }
  },

  computed: {
    raceSkills() {
      return this.character.race.skills.filter((skill, index) => {
        return index + 1 <= this.character.raceLevel.value
      })
    },

    classSkills() {
      return this.character.class.skills.filter((skill, index) => {
        return index + 1 <= this.character.classLevel.value
      })
    },

    specialSkill() {
      return this.character.race.special
    },

    showSpecialSkill() {
      return (
        this.specialSkill &&
        this.specialSkill.value <= this.character.raceLevel.value
      )
    }
  },

  methods: {
    getAttributeLabel,
    getSkillTypeLabel,
    getSkillCostDescription,

    getAttribute(attribute) {
      let result = 0
      const classAttribute = this.character.class[attribute]
      const isArrayAttribute = Array.isArray(classAttribute)

      result += this.character.race[attribute]
      result += this.character.raceLevel[attribute]
      result += isArrayAttribute
        ? getRandomWithRange(classAttribute[0], classAttribute[1])
        : getRandomWithRange(classAttribute)
      result += this.character.classLevel[attribute]

      result += this.character.primaryWeapon[attribute] || 0
      result += this.character.secondaryWeapon[attribute] || 0
      result += this.character.armor[attribute] || 0
      result += this.character.accessory[attribute] || 0

      return result
    },

    getBonusAttribute(attribute) {
      let result = 0

      result += this.character.primaryWeapon[attribute] || 0
      result += this.character.secondaryWeapon[attribute] || 0
      result += this.character.armor[attribute] || 0
      result += this.character.accessory[attribute] || 0

      return result
    }
  }
}
</script>
